import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Typography, Space, Card, Radio, Alert, Tooltip, Badge, Tag } from 'antd';
import {
  ReloadOutlined,
  UndoOutlined,
  BulbOutlined,
  DragOutlined,
  RotateRightOutlined,
  SwapOutlined,
  ExpandOutlined
} from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

// --- Helper Functions (Statistical & Geometric Correctness) ---
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const isFiniteNum = (v) => Number.isFinite(v);

const centroid = (pts) => {
  if (!pts?.length) return { x: 0, y: 0 };
  let sx = 0, sy = 0;
  for (const p of pts) { sx += p.x; sy += p.y; }
  return { x: sx / pts.length, y: sy / pts.length };
};

const applyTranslation = (pts, dx, dy) => pts.map((p) => ({ x: p.x + dx, y: p.y + dy }));
const applyRotation = (pts, c, angRad) => {
  const cos = Math.cos(angRad), sin = Math.sin(angRad);
  return pts.map((p) => {
    const x = p.x - c.x, y = p.y - c.y;
    return { x: c.x + x * cos - y * sin, y: c.y + x * sin + y * cos };
  });
};

const applyReflection = (pts, a, b) => {
  const abx = b.x - a.x, aby = b.y - a.y;
  const denom = abx * abx + aby * aby;
  if (denom === 0) return pts;
  return pts.map(p => {
    const t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / denom;
    const proj = { x: a.x + t * abx, y: a.y + t * aby };
    return { x: 2 * proj.x - p.x, y: 2 * proj.y - p.y };
  });
};

/**
 * ShapeShifter Flagship Component
 * - Interactive geometry puzzle using rigid motions
 * - Direct manipulation (drag-to-move/rotate/reflect)
 * - Integrated Ant Design UI & dynamic responsive viewBox
 */
export const ShapeShifter = ({ config, content }) => {
  const safe = config ?? {};
  const explanation = content?.explanation || "Map the blue shape to the target ghost using your allowed moves.";

  const start = useMemo(() => (safe.startShape || [[0,0],[2,0],[1,2]]).map(p => ({x: p[0], y: p[1]})), [safe.startShape]);
  const target = useMemo(() => (safe.targetShape || [[4,4],[6,4],[5,6]]).map(p => ({x: p[0], y: p[1]})), [safe.targetShape]);
  const allowedMoves = useMemo(() => Array.isArray(safe.allowedMoves) ? safe.allowedMoves : ["TRANSLATION", "ROTATION", "REFLECTION"], [safe.allowedMoves]);
  const maxMoves = safe.maxMoves || 3;

  // --- State ---
  const [tool, setTool] = useState(allowedMoves[0]);
  const [history, setHistory] = useState([]);
  const [committed, setCommitted] = useState(start);
  const [pending, setPending] = useState(null);
  const [alert, setAlert] = useState(null);
  const [rotCenter, setRotCenter] = useState(() => centroid(start));
  const [mirror, setMirror] = useState({ a: { x: 0, y: -2 }, b: { x: 0, y: 8 } });

  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const displayShape = useMemo(() => {
    if (!pending) return committed;
    if (pending.type === "TRANSLATION") return applyTranslation(committed, pending.dx, pending.dy);
    if (pending.type === "ROTATION") return applyRotation(committed, pending.center, pending.angleRad);
    return committed;
  }, [committed, pending]);

  // Dynamic ViewBox Calculation to ensure visibility
  const viewBox = useMemo(() => {
    const all = [...start, ...target, rotCenter, mirror.a, mirror.b];
    const xs = all.map(p => p.x);
    const ys = all.map(p => p.y);
    const minX = Math.min(...xs) - 2;
    const maxX = Math.max(...xs) + 2;
    const minY = Math.min(...ys) - 2;
    const maxY = Math.max(...ys) + 2;
    return `${minX} ${minY} ${maxX - minX} ${maxY - minY}`;
  }, [start, target, rotCenter, mirror]);

  const metrics = useMemo(() => {
    let sum = 0;
    displayShape.forEach((p, i) => { sum += Math.hypot(p.x - target[i]?.x, p.y - target[i]?.y); });
    const dist = sum / displayShape.length;
    const match = Math.round(clamp(100 - dist * 15, 0, 100));
    return { dist, matchPct: match };
  }, [displayShape, target]);

  // --- Interaction Logic ---
  const clientToSvg = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const sp = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: sp.x, y: sp.y };
  };

  const onPolygonDown = (e) => {
    if (tool !== "TRANSLATION" || history.length >= maxMoves) return;
    const p = clientToSvg(e);
    setDrag({ kind: "TRANSLATE", start: p });
    e.target.setPointerCapture(e.pointerId);
  };

  const onMove = (e) => {
    if (!drag) return;
    const p = clientToSvg(e);
    if (drag.kind === "TRANSLATE") {
      setPending({ type: "TRANSLATION", dx: p.x - drag.start.x, dy: p.y - drag.start.y });
    } else if (drag.kind === "ROTATE") {
      const startAng = Math.atan2(drag.start.y - rotCenter.y, drag.start.x - rotCenter.x);
      const curAng = Math.atan2(p.y - rotCenter.y, p.x - rotCenter.x);
      setPending({ type: "ROTATION", center: rotCenter, angleRad: curAng - startAng });
    } else if (drag.kind === "MOVE_CENTER") {
      setRotCenter(p);
    } else if (drag.kind === "MIRROR_A") {
      setMirror(prev => ({ ...prev, a: p }));
    } else if (drag.kind === "MIRROR_B") {
      setMirror(prev => ({ ...prev, b: p }));
    }
  };

  const onUp = () => {
    if (pending) {
      const next = pending.type === "TRANSLATION" ? applyTranslation(committed, pending.dx, pending.dy) : applyRotation(committed, pending.center, pending.angleRad);
      setCommitted(next);
      setHistory(h => [...h, pending]);
    }
    setDrag(null); setPending(null);
  };

  const reflectNow = () => {
    if (history.length >= maxMoves) return;
    const next = applyReflection(committed, mirror.a, mirror.b);
    setCommitted(next);
    setHistory(h => [...h, { type: "REFLECTION" }]);
  };

  const resetAll = () => { setCommitted(start); setHistory([]); setAlert(null); setPending(null); setRotCenter(centroid(start)); };
  const undo = () => { if (history.length > 0) setHistory(h => h.slice(0, -1)); /* Reset committed based on history logic would go here */ };

  return (
      <div className="ch-shapeshifter-widget animate-slide-up">
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">

          {/* Flagship Header with Ant Design Badge & Tag */}
          <Card size="small" className="ch-shapeshifter-header">
            <div className="flexSpaceBetween flexAlignCentre">
              <Space>
                <Title level={4} style={{ margin: 0 }}>Shape Shifter</Title>
                <Badge count={`Moves ${history.length}/${maxMoves}`} style={{ backgroundColor: '#1890ff' }} />
                <Tag color={metrics.matchPct > 90 ? "success" : "cyan"}>Match {metrics.matchPct}%</Tag>
              </Space>
              <Button icon={<ReloadOutlined />} size="small" onClick={resetAll}>Reset</Button>
            </div>
          </Card>

          {/* Pedagogical Intro Sentence */}
          <Paragraph type="secondary" italic style={{ marginBottom: 0 }}>
            {explanation}
          </Paragraph>

          {/* Success / Warning Alerts */}
          {metrics.matchPct >= 98 && (
              <Alert title="Excellent! You've matched the target shape." type="success" showIcon />
          )}

          {/* Interactive Puzzle Stage */}
          <div className="ch-shapeshifter-stage">
            <svg
                ref={svgRef}
                className="ch-shapeshifter-svg"
                viewBox={viewBox}
                onPointerMove={onMove}
                onPointerUp={onUp}
            >
              <polygon className="ch-shapeshifter-target" points={target.map(p=>`${p.x},${p.y}`).join(" ")} />

              {/* Show starting point ghost if moves have been made */}
              {history.length > 0 && safe.phantomMode !== false && (
                  <polygon className="ch-shapeshifter-start-ghost" points={start.map(p=>`${p.x},${p.y}`).join(" ")} fillOpacity="0.1" strokeDasharray="2" />
              )}

              <polygon
                  className="ch-shapeshifter-current"
                  points={displayShape.map(p=>`${p.x},${p.y}`).join(" ")}
                  onPointerDown={onPolygonDown}
                  style={{ cursor: tool === "TRANSLATION" ? 'move' : 'default' }}
              />

              {/* Direct Manipulation Handles */}
              {tool === "ROTATION" && (
                  <g>
                    <circle className="ch-shapeshifter-handle-center" cx={rotCenter.x} cy={rotCenter.y} r="0.3" onPointerDown={e => setDrag({kind:"MOVE_CENTER"})} />
                    <line x1={rotCenter.x} y1={rotCenter.y} x2={rotCenter.x + 2.5} y2={rotCenter.y} stroke="#1890ff" strokeDasharray="0.5" />
                    <circle className="ch-shapeshifter-handle" cx={rotCenter.x + 2.5} cy={rotCenter.y} r="0.4" onPointerDown={e => setDrag({kind:"ROTATE", start: clientToSvg(e)})} />
                  </g>
              )}

              {tool === "REFLECTION" && (
                  <g>
                    <line className="ch-shapeshifter-mirror-line" x1={mirror.a.x} y1={mirror.a.y} x2={mirror.b.x} y2={mirror.b.y} />
                    <circle className="ch-shapeshifter-handle" cx={mirror.a.x} cy={mirror.a.y} r="0.35" onPointerDown={e => setDrag({kind:"MIRROR_A"})} />
                    <circle className="ch-shapeshifter-handle" cx={mirror.b.x} cy={mirror.b.y} r="0.35" onPointerDown={e => setDrag({kind:"MIRROR_B"})} />
                  </g>
              )}
            </svg>
            <div className="ch-shapeshifter-stage-hint" style={{ position: 'absolute', bottom: 8, left: 16, fontSize: 11, opacity: 0.6 }}>
              {tool === "TRANSLATION" && "Drag the shape to move it."}
              {tool === "ROTATION" && "Drag the orange handle to rotate; red dot to move center."}
              {tool === "REFLECTION" && "Position the mirror line then click 'Apply Reflection'."}
            </div>
          </div>

          {/* Toolbar: Ant Design Button Groups for UI consistency */}
          <div className="flexSpaceBetween">
            <Radio.Group value={tool} onChange={e => setTool(e.target.value)} buttonStyle="solid">
              {allowedMoves.includes("TRANSLATION") && <Radio.Button value="TRANSLATION"><DragOutlined /> Move</Radio.Button>}
              {allowedMoves.includes("ROTATION") && <Radio.Button value="ROTATION"><RotateRightOutlined /> Rotate</Radio.Button>}
              {allowedMoves.includes("REFLECTION") && <Radio.Button value="REFLECTION"><SwapOutlined /> Reflect</Radio.Button>}
            </Radio.Group>

            <Space>
              <Button icon={<UndoOutlined />} onClick={undo} disabled={history.length === 0}>Undo</Button>
              <Tooltip title="Need help? Click for a hint.">
                <Button icon={<BulbOutlined />}>Hint</Button>
              </Tooltip>
              {tool === "REFLECTION" && (
                  <Button type="primary" onClick={reflectNow} disabled={history.length >= maxMoves}>Apply Reflection</Button>
              )}
            </Space>
          </div>
        </Space>
      </div>
  );
};